import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { AuditRow } from '../../lib/types';
import { Button, Card, Loading, PageHeader, Select } from '../../components/ui';
import { AuditDiff } from '../WorkOrderPage';

interface Verify {
  ok: boolean;
  count: number;
  lastSeq: number;
  lastHash: string;
  error?: { seq: number; reason: string };
}

const ENTITIES = ['', 'work_order', 'customer', 'customer_contact', 'machine', 'user', 'void_request', 'board', 'auth'];

export function AuditPage() {
  const [entity, setEntity] = useState('');
  const rows = useQuery({ queryKey: ['audit', entity], queryFn: () => api.get<AuditRow[]>(`/api/audit${qs({ entity, limit: 200 })}`) });
  const verify = useQuery({ queryKey: ['audit-verify'], queryFn: () => api.get<Verify>('/api/audit/verify'), enabled: false });

  return (
    <>
      <PageHeader title="Audit Log">
        <Select value={entity} onChange={(e) => setEntity(e.target.value)} className="w-56">
          {ENTITIES.map((e) => <option key={e} value={e}>{e || 'Everything'}</option>)}
        </Select>
        <Button variant="secondary" onClick={() => verify.refetch()}>Verify chain now</Button>
      </PageHeader>
      {verify.data && (
        <div className={`mb-4 rounded-md p-3 text-sm ${verify.data.ok ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
          {verify.data.ok
            ? `Chain intact: ${verify.data.count} rows verified, head seq ${verify.data.lastSeq} (${verify.data.lastHash.slice(0, 16)}…).`
            : `CHAIN BROKEN at seq ${verify.data.error?.seq}: ${verify.data.error?.reason}`}
        </div>
      )}
      <Card>
        {rows.isLoading ? <Loading /> : (
          <ul className="divide-y divide-slate-100 text-sm">
            {rows.data?.map((r) => (
              <li key={r.id} className="py-2">
                <div className="flex flex-wrap gap-x-3">
                  <span className="font-mono text-xs text-slate-400">#{r.seq}</span>
                  <span className="font-semibold">{r.entity}.{r.action}</span>
                  {r.entityId && <span className="text-slate-500">id {r.entityId}</span>}
                  <span>{r.userName ?? 'system'}</span>
                  <span className="text-slate-500">{dateTime(r.at)}</span>
                  <span className="text-slate-400">{r.ip}</span>
                </div>
                <AuditDiff before={r.beforeJson} after={r.afterJson} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
